import { useFormContext, useWatch } from "react-hook-form";
import { ScheduledReportsFormType, exportOptionRadioButtons, reportTypeRadioButtons } from "../ScheduledReportsEditorUtils";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, CardHeader, Stack, Typography, useTheme } from "@mui/material";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useReportConfigurationData } from "../../../queries/report/UseReportConfigurationData";
import { ReportDeliveryType, ReportTypeEnum } from "../../../services/reporting-data-service/ReportScheduleModels";
import { useGetGroupMarketingReportTats } from "../../../queries/group-marketing-report/UseGetGroupMarketingReportTats";
import { ScheduleSummaryCard } from "../../scheduled-reports-list/ScheduleSummaryCard";
import { useTimezones } from "../../../queries/time-zones/UseTimezones";
import ScheduledReportsSummaryUtils from "./ScheduledReportsSummaryUtils";
import { ScheduleNotificationsCard } from "../../scheduled-reports-list/ScheduleNotificationsCard";
import { getObjectTypeName } from "../../../common/utilities/GetObjectTypeName";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../forms/form-property/FormProperty";

import type { JSX } from "react";

export type ScheduledReportsSummaryProps = {
     groupID: number,
};

export default function ScheduledReportsSummary({
    groupID
}: ScheduledReportsSummaryProps) {

    const { t } = useTranslation();
    const theme = useTheme();
    const {data: timezones} = useTimezones();

    const {
        //getValues,
        watch,
        //trigger,
        control,
        //reset,
        //setValue,
        //resetField
    } = useFormContext<ScheduledReportsFormType>();    

    const scheduledReport = useWatch({ control: control }) as ScheduledReportsFormType;    

    const [
        schedules,
        recipients,
        notificationLists
    ] = watch([
        'schedules',
        'recipients',
        'notificationLists'
    ]);


    const {
        data: reportList
    } = useQueryNotifications(useReportConfigurationData)({
        groupID: groupID,
        enabled: scheduledReport.reportType !== ReportTypeEnum.GroupMarketing
    });

    const {
        data: tats
    } = useQueryNotifications(useGetGroupMarketingReportTats)({
        groupID: groupID,
        enabled: scheduledReport.reportType === ReportTypeEnum.GroupMarketing
    });


    const reportTypeName = reportTypeRadioButtons.find(o => o.value === scheduledReport.reportType)?.label;
    const tatName = tats?.find(o => o.id === scheduledReport.overrideTatID)?.name;
    const reportName = reportList?.configurations?.find(o => o.id === scheduledReport.reportConfigID)?.name;
    const exportFormat = exportOptionRadioButtons.find(o => o.value === scheduledReport.exportFormat)?.label;

    const activeSchedules = ScheduledReportsSummaryUtils.mapToScheduleSummaryModel(schedules.filter(o => o.isActive), timezones ?? []);
    const disabledSchedules = ScheduledReportsSummaryUtils.mapToScheduleSummaryModel(schedules.filter(o => !o.isActive), timezones ?? []);


    const notifications = ScheduledReportsSummaryUtils.mapToNotificationSummaryModel(recipients, notificationLists);

    const recipientItems = notifications.filter(o => o.deliveryType === ReportDeliveryType.Email);
    const notificationListItems = notifications.filter(o => o.deliveryType === ReportDeliveryType.NotificationList);

    const renderProperty = (label: string, value: string | undefined | JSX.Element | null) => (
        <FormProperty>
            <Stack direction='column'>
                <Typography variant='subtitle1'  sx={{
                    fontWeight: 600
                }}>
                   {label}
                </Typography>

                <Typography variant='body2'>
                    {value}
                </Typography>
            </Stack>
    </FormProperty>
    )


   return (
       <Box sx={{
           overflow: "hidden",
           overflowY: 'auto',
           padding: 1,
           height: '100%',
           backgroundColor: "custom.background.cardContainer",
       }}>
           <Card variant="outlined">
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title={t('scheduledReports.steps.summary.reportDetails')}
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>

               <CardContent sx={{
               }}>

                   <FormPropertySection label={undefined}>
                       {renderProperty('Report Type', reportTypeName)}
                       {renderProperty('Report Description', scheduledReport.description)}

                       {scheduledReport.reportType !== ReportTypeEnum.GroupMarketing ? 
                           renderProperty('Report', reportName  ?? "Not available") :
                           renderProperty('Report Tag', tatName)
                       }

                       {renderProperty('Export Format', exportFormat)}

                   </FormPropertySection>

               </CardContent>

           </Card>
           <Card variant="outlined" sx={{
               marginTop: 1,
           }}>
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title='Schedules'
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>

                   <CardContent sx={{
                       padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                       minHeight: theme.spacing(4)
                   }}>

                       {activeSchedules.map((o, ndx) => 
                           <ScheduleSummaryCard key={ndx} 
                               item={o} 
                               sx={{marginLeft: 1}} 
                           />
                       )}


                       {disabledSchedules.map((o, ndx) => 
                           <ScheduleSummaryCard key={ndx} 
                               item={o} 
                               sx={{marginLeft: 1}}                             
                           />
                       )}

                   </CardContent>
           </Card>
           <Card variant="outlined" sx={{
               marginTop: 1,
           }}>
               <CardHeader
                   sx={{
                       padding: 1
                   }}
                   title='Notifications'
                   slotProps={{
                       title: {
                           variant: 'h6'
                       }
                   }}
               >
               </CardHeader>

                   <CardContent sx={{
                       padding: (theme) => `0 ${theme.spacing(1)} ${theme.spacing(1)} ${theme.spacing(1)} !important`,
                       minHeight: theme.spacing(4)
                   }}>



                    {notificationListItems.length > 0 &&
                           <ScheduleNotificationsCard 
                               title={getObjectTypeName(eLynxObjectTypeEnum.NotificationList, true)} 
                               items={notificationListItems}
                               sx={{marginLeft: 1}}                             
                           />
                       }

                       {recipientItems.length > 0 &&
                           <ScheduleNotificationsCard 
                               title={getObjectTypeName(eLynxObjectTypeEnum.Recipient, true)} 
                               items={recipientItems}
                               sx={{marginLeft: 1}}                             
                           />
                       }

                   </CardContent>
               </Card>
       </Box>
   );
  }
